import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';

// ================ Async Thunks ================ //

/**
 * Fetch ALL own listings.
 * Recursively or iteratively calls the SDK to get past the 100-item limit.
 */
export const fetchAllOwnListings = createAsyncThunk(
  'listing/fetchAllOwnListings',
  async (_, { extra: sdk, rejectWithValue }) => {
    let allListings = [];
    let page = 1;
    let hasMore = true;

    try {
      while (hasMore) {
        const response = await sdk.ownListings.query({
          page,
          perPage: 100, // Max allowed by Sharetribe API
          include: ['images'], // Include commonly used relationships
        });

        const data = response.data.data;
        const meta = response.data.meta;

        // Combine new results with existing ones
        allListings = [...allListings, ...data];

        // Check if there are more pages
        if (page < meta.totalPages) {
          page++;
        } else {
          hasMore = false;
        }
      }

      return allListings;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

/**
 * Update a specific listing.
 * Accepts `id` and any other dynamic `params` to spread.
 */
export const updateOwnListing = createAsyncThunk(
  'listing/updateOwnListing',
  async ({ id, ...params }, { extra: sdk, rejectWithValue }) => {
    try {
      // 1. Call SDK Update
      const response = await sdk.ownListings.update({
        id,
        ...params, // Spreading dynamic parameters here
      });

      // 2. Return the updated entity data
      return response.data;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);
/////////////////////////////////////
// GENERIC BULK UPDATE THUNK       //
/////////////////////////////////////

export const updateAllListingsThunk = createAsyncThunk(
  'app/ManageListingsPage/updateAllListings',
  async (dataToUpdate, { dispatch, extra: sdk, rejectWithValue }) => {
    let allListings = [];
    let page = 1;
    let hasMore = true;

    try {
      // 1. FETCH ALL LISTINGS (Loop through pagination)
      // We need to get every ID before we can update them.
      while (hasMore) {
        const response = await sdk.ownListings.query({
          page,
          perPage: 100, // Maximize page size to reduce calls
          include: ['images'],
        });

        const data = response.data.data;
        const meta = response.data.meta;
        allListings = [...allListings, ...data];

        if (page < meta.totalPages) {
          page++;
        } else {
          hasMore = false;
        }
      }

      // 2. LOOP AND UPDATE EACH LISTING WITH DYNAMIC DATA
      // We map over the listings and apply the 'dataToUpdate' object to each one.
      const updatePromises = allListings.map(listing => {
        return sdk.ownListings.update({
          id: listing.id,
          ...dataToUpdate, // Spread the dynamic data here (e.g., publicData, price, etc.)
        });
      });

      // Execute all updates in parallel
      await Promise.all(updatePromises);

      return true;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);
// ================ Slice ================ //

const initialState = {
  ownListings: [],
  fetchInProgress: false,
  fetchError: null,
  updateInProgress: false,
  updateError: null,
};

const listingSlice = createSlice({
  name: 'listing',
  initialState,
  reducers: {},
  extraReducers: builder => {
    // --- Fetch All Listings ---
    builder.addCase(fetchAllOwnListings.pending, state => {
      state.fetchInProgress = true;
      state.fetchError = null;
    });
    builder.addCase(fetchAllOwnListings.fulfilled, (state, action) => {
      state.fetchInProgress = false;
      state.ownListings = action.payload;
    });
    builder.addCase(fetchAllOwnListings.rejected, (state, action) => {
      state.fetchInProgress = false;
      state.fetchError = action.payload;
    });

    // --- Update Listing ---
    builder.addCase(updateOwnListing.pending, state => {
      state.updateInProgress = true;
      state.updateError = null;
    });
    builder.addCase(updateOwnListing.fulfilled, (state, action) => {
      state.updateInProgress = false;
      const updatedListing = action.payload.data; // The returned entity

      // Optimistically update the listing in the local array
      // so we don't have to fetch all 100+ listings again.
      const index = state.ownListings.findIndex(l => l.id.uuid === updatedListing.id.uuid);
      if (index !== -1) {
        state.ownListings[index] = updatedListing;
      }
    });
    builder.addCase(updateOwnListing.rejected, (state, action) => {
      state.updateInProgress = false;
      state.updateError = action.payload;
    });

    // --- Update All Listings ---
    builder.addCase(updateAllListingsThunk.pending, state => {
      state.updateInProgress = true;
      state.updateError = null;
    });
    builder.addCase(updateAllListingsThunk.fulfilled, (state, action) => {
      state.updateInProgress = false;
    });
    builder.addCase(updateAllListingsThunk.rejected, (state, action) => {
      state.updateInProgress = false;
      state.updateError = action.payload;
    });
  },
});

// ================ Exports ================ //

export default listingSlice.reducer;

// ================ Selectors ================ //

export const getAllListings = state => state.listing.ownListings;
export const getListingFetchInProgress = state => state.listing.fetchInProgress;
export const getListingUpdateInProgress = state => state.listing.updateInProgress;
export const getListingError = state => state.listing.fetchError || state.listing.updateError;

// Helper selector to get a specific listing by ID from the store
export const getListingById = (state, listingId) => {
  return state.listing.ownListings.find(l => l.id.uuid === listingId);
};
